<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import KUIUnifiedAuthMfaExpired from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthMfaExpired.vue';
import KUIUnifiedAuthMfaSignOut from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthMfaSignOut.vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  loading: boolean;
  expired?: boolean;
  signedIn?: boolean;
}

withDefaults(defineProps<Props>(), {
  expired: false,
  signedIn: false,
});

const emit = defineEmits<{
  submitCode: [code: string];
  submitRecoveryCode: [code: string];
  startAgain: [];
  signOut: [];
}>();

const { t } = useI18n();

const usingRecoveryCode = ref(false);
const formResult = ref<ResponseResult | null>(null);

const codeFormConfig = defineFormConfig(
  {
    code: defineField({
      type: 'pincode',
      options: {
        required: true,
        label: t('kui.auth.mfaChallenge.codeLabel'),
        focusOnMounted: true,
        nextFocusOnSubmitButton: true,
      },
      initialValue: '',
      schema: z.string().length(6, t('general.formFields.otpCode.tooShort')).regex(/^\d+$/, t('general.formFields.otpCode.onlyNumbers')),
    }),
  },
  t('kui.auth.mfaChallenge.submit'),
);

const recoveryCodeFormConfig = defineFormConfig(
  {
    recoveryCode: defineField({
      type: 'text',
      options: {
        required: true,
        label: t('kui.auth.mfaChallenge.recoveryCodeLabel'),
        autocomplete: 'one-time-code',
        dataTestId: 'auth-mfa-recovery-code-input',
        maxLength: 64,
      },
      initialValue: '',
      schema: z.string().trim().min(1, t('kui.auth.mfaChallenge.recoveryCodeRequired')),
    }),
  },
  t('kui.auth.mfaChallenge.submit'),
);

const handleSubmitCode = (value: z.infer<typeof codeFormConfig.schema>) => {
  formResult.value = null;
  emit('submitCode', value.code);
};

const handleSubmitRecoveryCode = (value: z.infer<typeof recoveryCodeFormConfig.schema>) => {
  formResult.value = null;
  emit('submitRecoveryCode', value.recoveryCode.trim());
};

const clearError = () => {
  formResult.value = null;
};

const toggleRecoveryCode = () => {
  usingRecoveryCode.value = !usingRecoveryCode.value;
  clearError();
};

const setError = (message: string) => {
  formResult.value = { success: false, message };
};

defineExpose({ setError });
</script>

<template>
  <div
    class="flex flex-col items-center"
    data-testid="auth-mfa-challenge-step"
  >
    <Heading
      size="xl"
      as="h1"
      class="text-center mb-3"
    >
      {{ t('kui.auth.mfaChallenge.title') }}
    </Heading>
    <Text size="lg" class="text-center">
      {{ t('kui.auth.mfaChallenge.description') }}
    </Text>

    <KUIUnifiedAuthMfaExpired
      v-if="expired"
      class="mt-6"
      @start-again="emit('startAgain')"
    />
    <template v-else>
      <KUICoreForm
        v-if="usingRecoveryCode"
        key="recovery-code"
        class="mt-6 w-full"
        :form-config="recoveryCodeFormConfig"
        :form-result="formResult"
        :loading="loading"
        submit-button-size="lg"
        data-testid="auth-mfa-recovery-code-form"
        @submit="handleSubmitRecoveryCode"
      />
      <KUICoreForm
        v-else
        key="code"
        class="mt-6 w-full text-center"
        :form-config="codeFormConfig"
        :form-result="formResult"
        :loading="loading"
        submit-button-size="lg"
        data-testid="auth-mfa-code-form"
        @submit="handleSubmitCode"
      />
      <Button
        class="mt-4"
        variant="link"
        size="sm"
        type="button"
        data-testid="auth-mfa-toggle-recovery-code"
        @click="toggleRecoveryCode"
      >
        {{ usingRecoveryCode ? t('kui.auth.mfaChallenge.useAuthenticatorCode') : t('kui.auth.mfaChallenge.useRecoveryCode') }}
      </Button>
    </template>

    <KUIUnifiedAuthMfaSignOut
      v-if="signedIn"
      :disabled="loading"
      @sign-out="emit('signOut')"
    />
  </div>
</template>
